import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import { useEditor, EditorContent, BubbleMenu } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import Highlight from '@tiptap/extension-highlight'
import Link from '@tiptap/extension-link'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Placeholder from '@tiptap/extension-placeholder'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import {
  Bold, Italic, UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Minus, Table as TableIcon,
  Undo2, Redo2, Save, ChevronDown, ChevronRight, ChevronLeft,
  Sparkles, Expand, Minimize2, RefreshCw, Loader2,
  BookMarked, FileDown, AlignLeft, Scissors, Lock, Unlock, ImageIcon,
  ChevronsRight,
} from 'lucide-react'
import api from '../../lib/api'
import { supabase } from '../../lib/supabase'
import { listChapters } from '../../lib/thesisApi'
import { useUIStore } from '../../store/uiStore'
import { deduplicateChapterContent, removeDuplicateSections } from '../../utils/deduplicateContent'
import { parseAndRenderVisuals, parseFigureBlocks, stripMalformedVisuals, missingVisuals } from '../../utils/parseThesisVisuals'
import { processFiguresFromMarkdown, syncChapterFigures, expandFigureTokens, buildSVGFigureToken, injectFigureAfterParagraph, fillMissingVisuals } from '../../utils/figurePipeline'
import { countVisualTokens } from '../../utils/parseThesisVisuals'
import { inlineExportMermaid } from '../../utils/export/mermaidRenderer'
import { ThesisFigureExtension } from '../../extensions/ThesisFigureExtension'
import { generateConceptualFigures } from '../../utils/conceptualFigureEngine'
import { generateResearchFigures, hasResearchData } from '../../utils/researchFigureEngine'
import { repairIncompleteStructures, detectIncompleteAcademicStructures } from '../../utils/semanticCompletionEngine'
import { invalidateApprovalOnEdit, EDITING_LOCKED_STATES, REGEN_BLOCKED_STATES } from '../../utils/chapterWorkflowEngine'
import { classifyEdit } from '../../utils/chapterChangeClassifier'
import { useChapterStore } from '../../store/chapterStore'
import { useAIStore } from '../../store/aiStore'
import { streamAnthropic, generateAnthropic } from '../../lib/anthropic'
import { DEFAULT_VISUAL_LEVEL, VISUAL_LEVELS, clampVisualLevel, describeVisualLevel, planSectionVisuals, visualInstructionFor } from '../../utils/visualLevels'
import { buildVisualChecklist } from '../../utils/visualChecklist'
import ChapterStatusBadge from '../../components/ChapterStatusBadge'
import SectionProfileBadge from '../../components/SectionProfileBadge'
import HumanizePanel from '../../components/HumanizePanel'
import toast from 'react-hot-toast'

marked.setOptions({ breaks: false, gfm: true })

// ─── Resizable side panel ───────────────────────────────────────────────────
// Drag-to-resize for the outline and tools rails — width persists per browser
// so a user's preferred layout survives reloads.
function useResizablePanel(defaultWidth, { min, max, storageKey, direction = 'right' }) {
  const [width, setWidth] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(storageKey))
      return saved >= min && saved <= max ? saved : defaultWidth
    } catch { return defaultWidth }
  })
  const liveWidth = useRef(width)

  const startDrag = useCallback((e) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = liveWidth.current
    const onMove = (ev) => {
      const delta = direction === 'left' ? startX - ev.clientX : ev.clientX - startX
      const next  = Math.min(max, Math.max(min, startWidth + delta))
      liveWidth.current = next
      setWidth(next)
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      try { localStorage.setItem(storageKey, String(liveWidth.current)) } catch { /* ignore */ }
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [min, max, direction, storageKey])

  return [width, startDrag]
}

function ResizeHandle({ onMouseDown }) {
  return (
    <div
      onMouseDown={onMouseDown}
      className="shrink-0 group"
      style={{ width: 5, cursor: 'col-resize', background: 'transparent' }}
    >
      <div className="w-full h-full transition-colors group-hover:bg-brand-400/50" style={{ background: 'transparent' }} />
    </div>
  )
}

// ─── Legacy format detection ──────────────────────────────────────────────────
// Chapters written before the inline figure pipeline (pre-004 migration) may
// still have ACDYON_FIGURES: prefix or raw [FIGURE_START] markdown in the DB.
// These are converted on first load so subsequent saves use the new format.

const LEGACY_PREFIX = 'ACDYON_FIGURES:'
const LEGACY_SEP    = '\n---CONTENT---\n'

function detectLegacyContent(raw) {
  if (!raw) return false
  // [TABLE_START] counts too. Checking only for figures meant a chapter left
  // holding raw table blocks — an interrupted generation of a section whose
  // visual budget was tables — never migrated, so its tables stayed visible as
  // raw markup forever and never reached thesis_figures.
  return raw.startsWith(LEGACY_PREFIX) || raw.includes('[FIGURE_START]') || raw.includes('[TABLE_START]')
}

// Chapter-level HTML export (single chapter, opens in browser)
async function buildChapterExportHtml(editorHtml, chapterTitle) {
  // expandFigureTokens converts <div data-thesis-figure> to mermaid pre blocks;
  // inlineExportMermaid then turns those into finished SVG, so the file needs no
  // network and the print-to-PDF path is not racing a CDN download.
  const expanded = await inlineExportMermaid(expandFigureTokens(editorHtml))
  const title    = chapterTitle || 'Chapter'
  return `<!DOCTYPE html>
<html lang="en"><head>
<meta charset="utf-8"><title>${title}</title>
<style>
body{font-family:"Times New Roman",Georgia,serif;font-size:13pt;line-height:2;max-width:860px;margin:60px auto;padding:0 60px;color:#1a1410}
h1{font-size:15pt;font-weight:bold;text-align:center;text-transform:uppercase;margin:40px 0 24px}
h2{font-size:13pt;font-weight:bold;margin:32px 0 12px}
h3{font-size:13pt;font-weight:bold;font-style:italic;margin:24px 0 8px}
h4{font-size:13pt;font-weight:bold;margin:20px 0 8px}
p{margin-bottom:1em;text-align:justify}
table{width:100%;border-collapse:collapse;margin:24px 0;font-size:11pt}
th{background:#f0ece4;font-weight:600;border:1px solid #c5b89a;padding:8px 12px}
td{border:1px solid #c5b89a;padding:8px 12px}
blockquote{border-left:3px solid #2563eb;padding-left:16px;font-style:italic;color:#475569;margin:16px 0}
ul,ol{padding-left:24px;margin-bottom:1em}
.thesis-fig{margin:2rem 0;padding:1.25rem;background:#f8f6f2;border:1px solid #d9d0c3;border-radius:6px;text-align:center;max-width:100%}
/* Diagrams carry explicit pixel widths; without this they hang off the right margin when printed. */
.thesis-fig svg,.thesis-fig img{max-width:100%;height:auto;display:block;margin:0 auto}
.thesis-fig table{max-width:100%;table-layout:fixed;word-wrap:break-word}
.thesis-fig pre.mermaid{text-align:left;background:#f8f6f2;border:none;padding:0}
</style></head>
<body>${expanded}</body></html>`
}

const DOMPUR_CONFIG = {
  ADD_TAGS: ['svg', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
             'text', 'tspan', 'defs', 'use', 'g', 'marker', 'figure', 'figcaption'],
  ADD_ATTR: ['viewBox', 'xmlns', 'fill', 'stroke', 'stroke-width', 'stroke-dasharray',
             'marker-end', 'marker-start', 'd', 'cx', 'cy', 'r', 'rx', 'ry',
             'x', 'y', 'x1', 'y1', 'x2', 'y2', 'points', 'transform',
             'text-anchor', 'dominant-baseline', 'font-size', 'font-family',
             'class', 'style', 'width', 'height', 'refX', 'refY', 'orient',
             'markerWidth', 'markerHeight', 'preserveAspectRatio',
             // Inline figure token attributes (Part 1 pipeline)
             'data-thesis-figure', 'data-figure-id', 'data-figure-type',
             'data-figure-code', 'data-figure-title', 'data-figure-caption'],
}

function looksLikeMarkdown(text) {
  return /^#{1,4}\s/m.test(text) || /\*\*[^*]+\*\*/m.test(text) || /^\* /m.test(text)
}

async function markdownOrHTMLToHTML(content) {
  if (!content) return ''
  const withVisuals = parseAndRenderVisuals(content)
  if (looksLikeMarkdown(withVisuals)) {
    const html = await marked.parse(withVisuals, { async: false })
    return DOMPurify.sanitize(html, DOMPUR_CONFIG)
  }
  return DOMPurify.sanitize(withVisuals, DOMPUR_CONFIG)
}

// ─── Editor styles injected globally ─────────────────────────────────────────

const EDITOR_STYLES = `
.tiptap-scholar {
  outline: none;
  caret-color: #1A1410;
}
.tiptap-scholar p { margin-bottom: 1em; }
.tiptap-scholar h1 {
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
  margin: 2.5rem 0 1.5rem;
  color: #0f172a;
}
.tiptap-scholar h2 {
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: 1.125rem;
  font-weight: 700;
  text-align: left;
  margin: 2rem 0 0.75rem;
  color: #0f172a;
}
.tiptap-scholar h3 {
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: 1rem;
  font-weight: 700;
  font-style: italic;
  text-align: left;
  margin: 1.5rem 0 0.5rem;
  color: #0f172a;
}
.tiptap-scholar h4 {
  font-family: 'Bricolage Grotesque', system-ui, sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  text-align: left;
  margin: 1.25rem 0 0.5rem;
  color: #0f172a;
}
.tiptap-scholar ul, .tiptap-scholar ol { padding-left: 1.5rem; margin-bottom: 1em; }
.tiptap-scholar li { margin-bottom: 0.25em; }
.tiptap-scholar blockquote {
  border-left: 3px solid #2563eb;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #475569;
}
.tiptap-scholar table {
  width: 100%;
  border-collapse: collapse;
  margin: 1.5rem 0;
  font-size: 0.9rem;
}
.tiptap-scholar td, .tiptap-scholar th {
  border: 1px solid #c5b89a;
  padding: 0.5rem 0.75rem;
}
.tiptap-scholar th { background: #ede8df; font-weight: 600; }
.tiptap-scholar p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: left;
  color: #b0a898;
  pointer-events: none;
  height: 0;
}
.tiptap-scholar mark { background: rgba(37,99,235,0.25); }
.tiptap-scholar figure.thesis-figure,
.tiptap-scholar figure.thesis-table-wrapper {
  margin: 2rem 0;
  padding: 1.25rem;
  background: #f8f6f2;
  border: 1px solid #d9d0c3;
  border-radius: 6px;
  text-align: center;
}
.tiptap-scholar .thesis-figure-title {
  font-weight: 700;
  font-size: 0.9rem;
  color: #1e293b;
  margin-bottom: 0.75rem;
  display: block;
}
.tiptap-scholar .thesis-figure-caption {
  font-style: italic;
  font-size: 0.8rem;
  color: #64748b;
  margin-top: 0.75rem;
  display: block;
}
.tiptap-scholar .thesis-figure-svg svg {
  max-width: 100%;
  height: auto;
}
.tiptap-scholar .thesis-table {
  width: 100%;
  border-collapse: collapse;
  margin: 0.5rem 0;
  font-size: 0.875rem;
  text-align: left;
}
.tiptap-scholar .thesis-table th {
  background: #ede8df;
  font-weight: 600;
  border: 1px solid #c5b89a;
  padding: 0.5rem 0.75rem;
}
.tiptap-scholar .thesis-table td {
  border: 1px solid #c5b89a;
  padding: 0.5rem 0.75rem;
}
`

// ─── Toolbar button ───────────────────────────────────────────────────────────

function TBtn({ onClick, active, disabled, children, title, gold = false }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      className="flex items-center justify-center w-7 h-7 rounded transition-colors disabled:opacity-40"
      style={{
        background: active ? (gold ? 'rgba(37,99,235,0.2)' : 'rgba(255,255,255,0.12)') : 'transparent',
        color: active ? (gold ? '#2563eb' : '#0C1629') : gold ? '#2563eb' : '#506280',
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.07)' }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
    >
      {children}
    </button>
  )
}

function TSep() {
  return <div className="w-px h-5 mx-1 shrink-0" style={{ background: '#DDE4EF' }} />
}

// ─── Sticky Toolbar ───────────────────────────────────────────────────────────

function EditorToolbar({ editor, wordCount, saving, onSave, onInlineTransform, transforming, thesisId, chapterTitle, prevChapter, nextChapter, onGoToChapter }) {
  if (!editor) return null

  const H = ({ level }) => (
    <TBtn
      onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
      active={editor.isActive('heading', { level })}
      title={`Heading ${level}`}
    >
      <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'JetBrains Mono' }}>H{level}</span>
    </TBtn>
  )

  return (
    <div
      className="sticky top-0 z-20 flex items-center gap-0.5 px-4 overflow-x-auto shrink-0"
      style={{ background: '#FFFFFF', borderBottom: '1px solid #DDE4EF', height: 52, minWidth: 0 }}
    >
      {/* Back to chapters */}
      {thesisId && (
        <a
          href={`/app/thesis/${thesisId}/chapters`}
          className="flex items-center gap-1 px-2 py-1 rounded-md mr-2 shrink-0 font-sans text-xs transition-colors"
          style={{ color: '#506280', border: '1px solid #e2e8f0' }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <ChevronLeft style={{ width: 12, height: 12 }} />
          Chapters
        </a>
      )}

      {/* Prev/next chapter — switch without going back to the chapter list */}
      <button
        onClick={() => prevChapter && onGoToChapter(prevChapter.id)}
        disabled={!prevChapter}
        title={prevChapter ? `Chapter ${prevChapter.chapter_number}: ${prevChapter.title}` : 'No previous chapter'}
        className="flex items-center justify-center w-6 h-6 rounded-md shrink-0 transition-colors disabled:opacity-30"
        style={{ color: '#506280' }}
        onMouseEnter={e => { if (prevChapter) e.currentTarget.style.background = '#f8fafc' }}
        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      >
        <ChevronLeft style={{ width: 14, height: 14 }} />
      </button>
      <button
        onClick={() => nextChapter && onGoToChapter(nextChapter.id)}
        disabled={!nextChapter}
        title={nextChapter ? `Chapter ${nextChapter.chapter_number}: ${nextChapter.title}` : 'No next chapter'}
        className="flex items-center justify-center w-6 h-6 rounded-md mr-2 shrink-0 transition-colors disabled:opacity-30"
        style={{ color: '#506280' }}
        onMouseEnter={e => { if (nextChapter) e.currentTarget.style.background = '#f8fafc' }}
        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
      >
        <ChevronRight style={{ width: 14, height: 14 }} />
      </button>
      <TSep />

      {/* Headings */}
      <H level={1} /><H level={2} /><H level={3} /><H level={4} />
      <TSep />

      {/* Inline */}
      <TBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Bold">
        <Bold style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italic">
        <Italic style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Underline">
        <UnderlineIcon style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Strikethrough">
        <Strikethrough style={{ width: 13, height: 13 }} />
      </TBtn>
      <TSep />

      {/* Lists */}
      <TBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Bullet list">
        <List style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Numbered list">
        <ListOrdered style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} title="Blockquote">
        <Quote style={{ width: 13, height: 13 }} />
      </TBtn>
      <TSep />

      {/* Insert */}
      <TBtn onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table">
        <TableIcon style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontal rule">
        <Minus style={{ width: 13, height: 13 }} />
      </TBtn>
      <TSep />

      {/* AI tools */}
      {['expand', 'simplify', 'rephrase'].map(mode => (
        <TBtn
          key={mode}
          gold
          onClick={() => onInlineTransform(mode)}
          disabled={transforming}
          title={mode.charAt(0).toUpperCase() + mode.slice(1)}
        >
          <span style={{ fontSize: 10, fontWeight: 600, fontFamily: 'Sora', textTransform: 'capitalize' }}>
            {transforming ? '…' : mode === 'expand' ? '↕' : mode === 'simplify' ? '↓' : '↺'}
          </span>
        </TBtn>
      ))}
      <TSep />

      {/* History */}
      <TBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        <Undo2 style={{ width: 13, height: 13 }} />
      </TBtn>
      <TBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        <Redo2 style={{ width: 13, height: 13 }} />
      </TBtn>

      {/* Right side */}
      <div className="ml-auto flex items-center gap-3 shrink-0">
        <span className="font-mono text-xs text-acdyon-textFaint">{wordCount.toLocaleString()} words</span>
        <button
          onClick={onSave}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-sans text-xs font-medium transition-all"
          style={{ background: saving ? 'rgba(61,184,122,0.1)' : 'rgba(37,99,235,0.1)', color: saving ? '#3DB87A' : '#2563eb', border: `1px solid ${saving ? 'rgba(61,184,122,0.3)' : 'rgba(37,99,235,0.3)'}` }}
        >
          <Save style={{ width: 12, height: 12 }} />
          {saving ? 'Saved' : 'Save'}
        </button>
      </div>
    </div>
  )
}

// ─── Bubble Menu (floating toolbar on text selection) ────────────────────────

function FloatingAIMenu({ editor, onTransform, transforming }) {
  if (!editor) return null

  return (
    <BubbleMenu
      editor={editor}
      tippyOptions={{ duration: 100 }}
      shouldShow={({ editor, from, to }) => from !== to && !editor.isActive('code')}
    >
      <div
        className="flex items-center gap-0.5 px-2 py-1 rounded-full"
        style={{ background: '#FFFFFF', border: '1px solid rgba(37,99,235,0.3)', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}
      >
        {['expand', 'simplify', 'rephrase'].map(mode => (
          <button
            key={mode}
            type="button"
            onClick={() => onTransform(mode)}
            disabled={transforming}
            className="flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-xs font-medium transition-colors disabled:opacity-50"
            style={{ color: '#2563eb' }}
            onMouseEnter={e => (e.currentTarget.style.background = 'rgba(37,99,235,0.1)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <Sparkles style={{ width: 10, height: 10 }} />
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
          </button>
        ))}
        <div className="w-px h-4 mx-1" style={{ background: '#DDE4EF' }} />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          className="flex items-center gap-1 px-2.5 py-1 rounded-full font-sans text-xs font-medium transition-colors"
          style={{ color: '#506280' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.05)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          Highlight
        </button>
      </div>
    </BubbleMenu>
  )
}

// ─── Left panel — section navigator ──────────────────────────────────────────

function SectionNavigator({ chapter, wordCount }) {
  const [collapsed, setCollapsed] = useState(false)
  const [width, startDrag] = useResizablePanel(240, { min: 180, max: 420, storageKey: 'acdyon_outline_width', direction: 'right' })
  const tocSections = chapter?.toc_sections?.sections || chapter?.toc_sections || []

  // Collapsing used to shrink this panel to width:0 — which also hid the
  // only button that could undo it, stranding the user with no way back
  // short of leaving and re-entering the chapter. A slim rail with just the
  // expand control stays clickable instead.
  if (collapsed) {
    return (
      <div
        className="flex flex-col shrink-0 items-center pt-3"
        style={{ width: 28, background: '#FFFFFF', borderRight: '1px solid #DDE4EF' }}
      >
        <button onClick={() => setCollapsed(false)} title="Show chapter outline">
          <ChevronsRight style={{ width: 14, height: 14, color: '#8EA4BC' }} />
        </button>
      </div>
    )
  }

  return (
    <>
    <div
      className="flex flex-col shrink-0 overflow-hidden"
      style={{ width, background: '#FFFFFF', borderRight: '1px solid #DDE4EF' }}
    >
      <div className="flex items-center justify-between px-4 py-3 shrink-0" style={{ borderBottom: '1px solid #DDE4EF' }}>
        <span className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide">
          Chapter Outline
        </span>
        <button onClick={() => setCollapsed(true)} title="Collapse chapter outline">
          <ChevronRight style={{ width: 13, height: 13, color: '#8EA4BC', transform: 'rotate(180deg)' }} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {/* Chapter title */}
        <div className="px-2 py-2 mb-1">
          <p className="font-sans text-xs font-semibold text-acdyon-text leading-snug">{chapter?.title}</p>
          <p className="font-mono text-xs text-acdyon-textFaint mt-0.5">{wordCount.toLocaleString()} words</p>
        </div>

        {tocSections.map(sec => (
          <div key={sec.id}>
            <div
              className="flex items-start gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors hover:bg-acdyon-surface"
              onClick={() => {
                // Scroll editor to h2 matching section title
                const headings = document.querySelectorAll('.tiptap-scholar h2')
                for (const h of headings) {
                  if (h.textContent.includes(sec.title.split(':')[0])) {
                    h.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    break
                  }
                }
              }}
            >
              <span className="font-mono text-xs shrink-0 mt-0.5" style={{ color: '#2563eb', minWidth: 28 }}>{sec.number}</span>
              <span className="font-sans text-xs text-acdyon-textMuted leading-snug">{sec.title}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
    <ResizeHandle onMouseDown={startDrag} />
    </>
  )
}

// ─── Right panel ──────────────────────────────────────────────────────────────

// Visual density slider — controls how many figures and tables generation puts
// in THIS chapter. Labelled with the real counts so the number means something.

function VisualDensitySlider({ value, onChange, disabled }) {
  return (
    <div className="p-4" style={{ borderBottom: '1px solid #DDE4EF' }}>
      <div className="flex items-baseline justify-between mb-2">
        <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide">Visuals</p>
        <span className="font-mono text-xs" style={{ color: '#2563eb' }}>{value}/5</span>
      </div>

      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={value}
        disabled={disabled}
        onChange={e => onChange(Number(e.target.value))}
        aria-label="Figures and tables per chapter"
        className="w-full"
        style={{ accentColor: '#2563eb', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1 }}
      />

      <div className="flex justify-between px-0.5 mt-0.5" aria-hidden="true">
        {[1, 2, 3, 4, 5].map(n => (
          <span key={n} className="font-mono" style={{ fontSize: 9, color: n === value ? '#2563eb' : '#8EA4BC' }}>{n}</span>
        ))}
      </div>

      <p className="font-sans text-xs mt-1.5" style={{ color: '#506280' }}>
        {describeVisualLevel(value)} <span style={{ color: '#8EA4BC' }}>per chapter</span>
      </p>
      <p className="font-sans mt-1" style={{ fontSize: 10, color: '#8EA4BC', lineHeight: 1.5 }}>
        Applies the next time this chapter is generated.
      </p>
    </div>
  )
}

// A labelled on/off switch. Each choice is remembered in this browser and applies the next time the chapter is generated.
function SwitchRow({ title, help, value, onChange, disabled }) {
  return (
    <div className="p-4" style={{ borderBottom: '1px solid #DDE4EF' }}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide">{title}</p>
          <p className="font-sans mt-1" style={{ fontSize: 10, color: '#8EA4BC', lineHeight: 1.5 }}>{help}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={value}
          aria-label={title}
          disabled={disabled}
          onClick={() => onChange(!value)}
          className="shrink-0 rounded-full transition-colors"
          style={{ width: 36, height: 20, position: 'relative', background: value ? '#2563eb' : '#C5D0E0', opacity: disabled ? 0.5 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <span className="rounded-full transition-all" style={{ position: 'absolute', top: 2, left: value ? 18 : 2, width: 16, height: 16, background: '#FFFFFF' }} />
        </button>
      </div>
    </div>
  )
}

function RightPanel({ chapter, thesisId, wordCount, status, onStatusChange, saving, lastSaved, onDeduplicate, onExport, onRegenFigures, regenFiguresLoading, onConceptualFigures, conceptualFiguresLoading, onResearchFigures, researchFiguresLoading, hasResearchResults, onRepairSemantic, repairSemanticLoading, isEditingLocked, visualDensity, onVisualDensityChange, harnessWriter, onHarnessWriterChange, harnessPolish, onHarnessPolishChange, onRegenerateChapter, getText }) {
  const [notes, setNotes] = useState(chapter?.user_notes || '')
  const [exportOpen, setExportOpen] = useState(false)
  const target   = chapter?.target_words || 0
  const pct      = target > 0 ? Math.min(Math.round((wordCount / target) * 100), 100) : 0
  const pages    = Math.round(wordCount / 250)
  const [width, startDrag] = useResizablePanel(280, { min: 220, max: 440, storageKey: 'acdyon_toolspanel_width', direction: 'left' })

  return (
    <>
    <ResizeHandle onMouseDown={startDrag} />
    <div
      className="flex flex-col shrink-0 overflow-y-auto"
      style={{ width, background: '#FFFFFF', borderLeft: '1px solid #DDE4EF' }}
    >
      {/* Word count */}
      <div className="p-4 space-y-4" style={{ borderBottom: '1px solid #DDE4EF' }}>
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="font-mono text-2xl font-semibold text-brand-600">{wordCount.toLocaleString()}</span>
            <span className="font-sans text-xs text-acdyon-textFaint">/ {target.toLocaleString()}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden mb-1" style={{ background: '#1E3A5F' }}>
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, background: pct >= 100 ? '#3DB87A' : '#2563eb' }}
            />
          </div>
          <div className="flex justify-between">
            <span className="font-sans text-xs text-acdyon-textFaint">{pct}% of target</span>
            <span className="font-mono text-xs text-acdyon-textFaint">{pages} pp</span>
          </div>
        </div>

        {/* Save status */}
        <div className="flex items-center gap-1.5">
          {saving ? (
            <>
              <span className="w-2 h-2 rounded-full bg-brand-600 animate-pulse" />
              <span className="font-sans text-xs text-acdyon-textFaint">Saving…</span>
            </>
          ) : lastSaved ? (
            <>
              <span className="w-2 h-2 rounded-full" style={{ background: '#3DB87A' }} />
              <span className="font-sans text-xs text-acdyon-textFaint">Saved {lastSaved}</span>
            </>
          ) : null}
        </div>
      </div>

      <VisualDensitySlider
        value={visualDensity}
        onChange={onVisualDensityChange}
        disabled={isEditingLocked}
      />

      <SwitchRow
        title="Enhanced writer"
        help="Studies real theses to write in a more natural style, and checks citations against real papers. Slower. Figures and tables are added by the standard writer."
        value={harnessWriter}
        onChange={onHarnessWriterChange}
        disabled={isEditingLocked}
      />
      <SwitchRow
        title="Polish"
        help="One extra pass that rewrites each finished section into plainer, more varied wording. Adds time and cost. Off by default."
        value={harnessPolish}
        onChange={onHarnessPolishChange}
        disabled={isEditingLocked}
      />

      {/* Chapter status lifecycle */}
      <div className="p-4" style={{ borderBottom: '1px solid #DDE4EF' }}>
        <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide mb-2">Status</p>
        <ChapterStatusBadge
          thesisId={thesisId}
          chapterId={chapter?.id}
          status={status}
          onStatusChange={onStatusChange}
        />
      </div>

      {/* Writing profile */}
      <div className="p-4" style={{ borderBottom: '1px solid #DDE4EF' }}>
        <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide mb-2">Writing Profile</p>
        <SectionProfileBadge chapterTitle={chapter?.title} chapterNumber={chapter?.chapter_number} />
      </div>

      {/* Tools */}
      <div className="p-4 space-y-3" style={{ borderBottom: '1px solid #DDE4EF' }}>
        <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide">Tools</p>
        {status !== 'NOT_STARTED' && status !== 'GENERATING' && (
          <button
            onClick={onRegenerateChapter}
            disabled={isEditingLocked}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg font-sans text-sm transition-colors disabled:opacity-40"
            style={{ color: '#dc2626', border: '1px solid rgba(220,38,38,0.3)' }}
            onMouseEnter={e => { if (!isEditingLocked) e.currentTarget.style.background = 'rgba(220,38,38,0.06)' }}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            title={isEditingLocked ? 'Revert to Draft to regenerate' : 'Rewrite this chapter from scratch — replaces all current content'}
          >
            <RefreshCw style={{ width: 14, height: 14 }} />
            Regenerate Chapter
          </button>
        )}
        <div className="relative">
          <button
            onClick={() => setExportOpen(o => !o)}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg font-sans text-sm text-acdyon-textMuted hover:text-acdyon-text transition-colors"
            style={{ border: '1px solid #DDE4EF' }}
          >
            <FileDown style={{ width: 14, height: 14 }} />
            Export Chapter
            <ChevronDown style={{ width: 12, height: 12, marginLeft: 'auto' }} />
          </button>
          {exportOpen && (
            <div
              className="absolute left-0 right-0 mt-1 rounded-lg shadow-lg z-50 overflow-hidden"
              style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              {[['html','HTML'], ['docx','Word (DOCX)'], ['pdf','PDF']].map(([fmt, label]) => (
                <button
                  key={fmt}
                  onClick={() => { setExportOpen(false); onExport(fmt) }}
                  className="w-full text-left px-4 py-2 font-sans text-sm transition-colors"
                  style={{ color: '#c8d0e0' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.08)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        <HumanizePanel getText={getText} />
        <button
          onClick={onRegenFigures}
          disabled={regenFiguresLoading || isEditingLocked}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg font-sans text-sm transition-colors disabled:opacity-40"
          style={{ color: '#0891b2', border: '1px solid rgba(8,145,178,0.3)' }}
          onMouseEnter={e => { if (!regenFiguresLoading && !isEditingLocked) e.currentTarget.style.background = 'rgba(8,145,178,0.06)' }}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          title={isEditingLocked ? 'Revert to Draft to enable figure regeneration' : 'Re-process chapter content to re-extract and re-render all inline figures'}
        >
          {regenFiguresLoading
            ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
            : <ImageIcon style={{ width: 14, height: 14 }} />}
          {regenFiguresLoading ? 'Regenerating…' : 'Regenerate Figures'}
        </button>
        {/* Pipeline A — conceptual figures (Ch1-3 only) */}
        {(chapter?.chapter_number || 0) <= 3 && (
          <button
            onClick={onConceptualFigures}
            disabled={conceptualFiguresLoading || isEditingLocked}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg font-sans text-sm transition-colors disabled:opacity-40"
            style={{ color: '#0891b2', border: '1px solid rgba(8,145,178,0.3)' }}
            onMouseEnter={e => { if (!conceptualFiguresLoading && !isEditingLocked) e.currentTarget.style.background = 'rgba(8,145,178,0.06)' }}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            title={isEditingLocked ? 'Revert to Draft to add figures' : 'Generate conceptual mermaid diagrams for this chapter'}
          >
            {conceptualFiguresLoading
              ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
              : <ImageIcon style={{ width: 14, height: 14 }} />}
            {conceptualFiguresLoading ? 'Generating…' : 'Conceptual Figures'}
          </button>
        )}

        {/* Pipeline B — research figures (Ch4+ only) */}
        {(chapter?.chapter_number || 0) >= 4 && (
          <button
            onClick={onResearchFigures}
            disabled={researchFiguresLoading || !hasResearchResults || isEditingLocked}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg font-sans text-sm transition-colors disabled:opacity-40"
            style={{ color: '#059669', border: '1px solid rgba(5,150,105,0.3)' }}
            onMouseEnter={e => { if (!researchFiguresLoading && hasResearchResults && !isEditingLocked) e.currentTarget.style.background = 'rgba(5,150,105,0.06)' }}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            title={isEditingLocked ? 'Revert to Draft to add figures' : hasResearchResults ? 'Generate SVG charts from Research Lab results' : 'Run the Research Lab first to enable this button'}
          >
            {researchFiguresLoading
              ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
              : <ImageIcon style={{ width: 14, height: 14 }} />}
            {researchFiguresLoading ? 'Injecting…' : hasResearchResults ? 'Research Figures' : 'Research Figures (no data)'}
          </button>
        )}

        <button
          onClick={onRepairSemantic}
          disabled={repairSemanticLoading || isEditingLocked}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg font-sans text-sm transition-colors disabled:opacity-40"
          style={{ color: '#7c3aed', border: '1px solid rgba(124,58,237,0.3)' }}
          onMouseEnter={e => { if (!repairSemanticLoading && !isEditingLocked) e.currentTarget.style.background = 'rgba(124,58,237,0.06)' }}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          title={isEditingLocked ? 'Revert to Draft to repair semantic gaps' : 'Detect and repair incomplete academic sentences'}
        >
          {repairSemanticLoading
            ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
            : <ImageIcon style={{ width: 14, height: 14 }} />}
          {repairSemanticLoading ? 'Repairing…' : 'Repair Semantic Gaps'}
        </button>
        <button
          onClick={onDeduplicate}
          disabled={isEditingLocked}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg font-sans text-sm transition-colors disabled:opacity-40"
          style={{ color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}
          onMouseEnter={e => { if (!isEditingLocked) e.currentTarget.style.background = 'rgba(245,158,11,0.06)' }}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          title={isEditingLocked ? 'Revert to Draft to deduplicate' : 'Remove duplicate paragraphs and sections'}
        >
          <Scissors style={{ width: 14, height: 14 }} />
          Fix Duplicate Content
        </button>
      </div>

      {/* Chapter notes */}
      <div className="p-4 flex-1 flex flex-col">
        <p className="font-sans text-xs font-semibold text-acdyon-textFaint uppercase tracking-wide mb-2">
          Private Notes
        </p>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Your private notes — not included in generated content…"
          rows={6}
          className="flex-1 px-3 py-2.5 rounded-lg font-sans text-xs text-acdyon-textMuted placeholder:text-acdyon-textFaint outline-none resize-none"
          style={{ background: '#112236', border: '1px solid #DDE4EF' }}
        />
      </div>
    </div>
    </>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ChapterEditorPage() {
  const { id, chapterId } = useParams()
  const [searchParams]    = useSearchParams()
  const navigate          = useNavigate()
  const aiStore           = useAIStore()

  const [thesis,              setThesis]              = useState(null)
  const [chapter,             setChapter]             = useState(null)
  const [loading,             setLoading]             = useState(true)
  const [saving,              setSaving]              = useState(false)
  const [lastSaved,           setLastSaved]           = useState('')
  const [status,              setStatus]              = useState('DRAFT')
  const [streaming,           setStreaming]           = useState(false)
  const [isGenerating,        setIsGenerating]        = useState(false)
  const [generationProgress,  setGenerationProgress]  = useState(0)
  const [generationError,     setGenerationError]     = useState('')
  const [visualChecklist,     setVisualChecklist]     = useState(null)
  const [transforming,        setTransforming]        = useState(false)
  const [wordCount,           setWordCount]           = useState(0)
  const [regenFiguresLoading,        setRegenFiguresLoading]        = useState(false)
  const [conceptualFiguresLoading,   setConceptualFiguresLoading]   = useState(false)
  const [researchFiguresLoading,     setResearchFiguresLoading]     = useState(false)
  const [repairSemanticLoading,      setRepairSemanticLoading]      = useState(false)
  const [fillingVisuals,             setFillingVisuals]             = useState(false)
  const [hasResearchResults,         setHasResearchResults]         = useState(false)
  const [visualDensity,              setVisualDensity]              = useState(DEFAULT_VISUAL_LEVEL)
  const [harnessWriter,              setHarnessWriter]              = useState(() => { try { return localStorage.getItem('acdyon_harness_writer') !== 'off' } catch { return true } })
  const [harnessPolish,              setHarnessPolish]              = useState(() => { try { return localStorage.getItem('acdyon_harness_polish') === 'on' } catch { return false } })
  const [siblingChapters,            setSiblingChapters]            = useState([])
  // null = fine as-is · 'missing' = never generated (safe to auto-start) ·
  // 'incomplete' = has content but short on figures/tables (must not be
  // auto-restarted — that would throw away good prose to fix a visual count).
  const [nextChapterNeedsWork,       setNextChapterNeedsWork]       = useState(null)

  // ── Auto-collapse the main app sidebar while writing/viewing a chapter —
  // this page is the one place the canvas needs the room. Restores whatever
  // the user had on the way out rather than forcing it back open.
  const setSidebarCollapsed = useUIStore(s => s.setSidebarCollapsed)
  useEffect(() => {
    const prevCollapsed = useUIStore.getState().sidebarCollapsed
    setSidebarCollapsed(true)
    return () => setSidebarCollapsed(prevCollapsed)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sibling chapters — for prev/next navigation and the "generate next
  // chapter" nudge. Refetched whenever the thesis changes, not on every
  // chapter switch within it, so paging through chapters stays snappy.
  useEffect(() => {
    if (!id) return
    listChapters(id).then(setSiblingChapters).catch(() => {})
  }, [id])

  const chapterIndex = useMemo(
    () => siblingChapters.findIndex(c => c.id === chapterId),
    [siblingChapters, chapterId]
  )
  const prevChapter = chapterIndex > 0 ? siblingChapters[chapterIndex - 1] : null
  const nextChapter = chapterIndex >= 0 && chapterIndex < siblingChapters.length - 1
    ? siblingChapters[chapterIndex + 1] : null

  // Does the next chapter still need generating (or finishing off its figures
  // and tables)? Checked from the same authoritative scan the checklist uses,
  // never a separate guess — re-run on every load so a fresh chapter list or
  // a since-completed next chapter is reflected without a full page reload.
  useEffect(() => {
    if (!nextChapter) { setNextChapterNeedsWork(null); return }
    let cancelled = false
    supabase.from('chapters').select('content, status, visual_density, chapter_number').eq('id', nextChapter.id).maybeSingle()
      .then(({ data: nc }) => {
        if (cancelled || !nc) return
        if (!nc.content?.trim()) { setNextChapterNeedsWork('missing'); return }
        if (nc.status === 'GENERATING') { setNextChapterNeedsWork(null); return }
        const result = buildVisualChecklist({
          html: nc.content,
          chapterNumber: nc.chapter_number || 1,
          level: clampVisualLevel(nc.visual_density),
        })
        setNextChapterNeedsWork(result.complete ? null : 'incomplete')
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [nextChapter?.id, status]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chapter store — subscribe to canonical status from any transition ────────
  const storeStatus = useChapterStore(state => state.chapters[chapterId]?.status)
  useEffect(() => {
    if (storeStatus && storeStatus !== status) setStatus(storeStatus)
  }, [storeStatus]) // eslint-disable-line react-hooks/exhaustive-deps

  const isEditingLocked = EDITING_LOCKED_STATES.has(status)
  const isRegenBlocked  = REGEN_BLOCKED_STATES.has(status)

  const saveTimerRef      = useRef(null)
  const saveRef           = useRef(null)   // always points to latest save() — fixes stale closure in scheduleSave
  const abortRef          = useRef(null)   // AbortController for current Anthropic fetch
  const generatingRef     = useRef(false)  // sync ref for stop-generation handler
  const previousHtmlRef   = useRef('')     // last saved HTML — used by change classifier
  const accumulatedRef    = useRef('')     // live text ref for interval-based word count
  const currentSectionRef = useRef(0)     // tracks which section is being generated (for pause)
  const resumePrefixRef   = useRef('')    // HTML already written before a resume — prepended on save

  // ── Live word count during generation (interval-based to bypass React 18 batching) ──

  useEffect(() => {
    if (!isGenerating) return
    const targetWC = chapter?.target_words || 4000
    const iv = setInterval(() => {
      const wc = accumulatedRef.current
        .replace(/[#*`>\-*_]/g, ' ')
        .trim()
        .split(/\s+/)
        .filter(Boolean).length
      if (wc > 0) {
        setWordCount(wc)
        setGenerationProgress(Math.min(94, Math.round((wc / targetWC) * 100)))
      }
    }, 400)
    return () => clearInterval(iv)
  }, [isGenerating, chapter?.target_words])

  // ── TipTap editor ────────────────────────────────────────────────────────

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Highlight,
      Link.configure({ openOnClick: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Placeholder.configure({ placeholder: 'Your chapter begins here…' }),
      ThesisFigureExtension, // inline figure nodes (Part 1 pipeline)
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'tiptap-scholar',
        spellcheck: 'true',
      },
    },
    onUpdate: ({ editor }) => {
      const text = editor.getText()
      const wc = text.trim() ? text.trim().split(/\s+/).length : 0
      setWordCount(wc)
      scheduleSave()
    },
  })

  // ── Load data — backend first, Supabase fallback ─────────────────────────

  useEffect(() => {
    if (!editor) return
    // Switching chapters (prev/next arrows, "Generate Next Chapter") reuses
    // this component instance rather than remounting it — without this the
    // previous chapter's checklist stayed on screen if the new one has no
    // content yet to recompute one from.
    setVisualChecklist(null)
    const load = async () => {
      try {
        let thesis, ch
        try {
          const [thesisRes, chapterRes] = await Promise.all([
            api.get(`/thesis/${id}`),
            api.get(`/thesis/${id}/chapters/${chapterId}`),
          ])
          if (!thesisRes.data?.id) throw new Error('invalid')
          thesis = thesisRes.data
          ch = chapterRes.data
        } catch {
          const [{ data: t }, { data: c }] = await Promise.all([
            supabase.from('theses').select('*').eq('id', id).maybeSingle(),
            supabase.from('chapters').select('*').eq('id', chapterId).maybeSingle(),
          ])
          thesis = t
          ch = c
        }
        setThesis(thesis)
        setChapter(ch)
        setStatus(ch?.status || 'DRAFT')
        // Falls back to the default when the visual_density column is absent.
        setVisualDensity(clampVisualLevel(ch?.visual_density))
        if (ch?.id) useChapterStore.getState().hydrate([ch])
        // Don't restore old content when streaming is about to overwrite it
        if (ch?.content && searchParams.get('streaming') !== '1') {
          let displayContent = ch.content

          // ── Legacy format migration ─────────────────────────────────────
          // Old chapters may have ACDYON_FIGURES: prefix or raw [FIGURE_START]
          // blocks. Process them through the new pipeline once to migrate.
          if (detectLegacyContent(displayContent)) {
            try {
              // Strip old prefix if present
              let rawMd = displayContent
              if (rawMd.startsWith(LEGACY_PREFIX)) {
                const sepIdx = rawMd.indexOf(LEGACY_SEP)
                rawMd = sepIdx !== -1 ? rawMd.slice(sepIdx + LEGACY_SEP.length) : rawMd.slice(LEGACY_PREFIX.length)
              }
              // Process through new pipeline (saves figures to DB, produces inline HTML)
              const chNum = ch.chapter_number || 1
              // Mixed content is normal here: a chapter interrupted mid-resume
              // holds tokenised HTML followed by raw blocks. Numbering has to
              // continue past the tokens already in it, or this migration mints
              // a second fig_<key>_1 and one of the pair stops being counted.
              displayContent = await processFiguresFromMarkdown(rawMd, supabase, {
                thesisId: id, chapterId, chapterNum: chNum, startAt: countVisualTokens(rawMd),
              })
              // Update DB with migrated content (best-effort, non-blocking)
              supabase.from('chapters').update({ content: displayContent }).eq('id', chapterId).then(() => {})
              syncChapterFigures(supabase, { chapterId, html: displayContent })
            } catch (e) {
              console.warn('[load] Legacy migration failed, showing as text:', e)
              const rawMd = displayContent.startsWith(LEGACY_PREFIX)
                ? displayContent.slice(displayContent.indexOf(LEGACY_SEP) + LEGACY_SEP.length)
                : displayContent
              displayContent = await markdownOrHTMLToHTML(rawMd)
            }
          }

          // ── New HTML format ─────────────────────────────────────────────
          // Content is HTML with <div data-thesis-figure> tokens.
          // TipTap's ThesisFigureExtension parses them into inline nodes.
          editor.commands.setContent(displayContent, false)
          previousHtmlRef.current = displayContent
          const wc = editor.getText().trim().split(/\s+/).filter(Boolean).length
          setWordCount(wc)

          // Recompute the checklist on every load of a settled chapter — it
          // previously only ever ran once, synchronously, right after a fresh
          // Generate call finished. Reopening an already-generated chapter (the
          // overwhelmingly common way this page is viewed) never recomputed
          // it, so the checklist looked like it had never been built at all.
          if (ch?.status && ch.status !== 'GENERATING') {
            setVisualChecklist(buildVisualChecklist({
              html: displayContent,
              chapterNumber: ch.chapter_number || 1,
              level: clampVisualLevel(ch?.visual_density),
            }))
          }
        }
      } catch {
        // silent
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [id, chapterId, editor]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── processAndFinalizeSave ─────────────────────────────────────────────────
  // Called after generation (or Stop/Pause) with raw accumulated markdown.
  // Extracts figure/table blocks → saves to thesis_figures DB → converts
  // remaining markdown to HTML → saves to chapters.content → updates editor.

  // `prefixHtml` carries the already-written part of a resumed chapter. This
  // save replaces chapters.content outright, so without it a resume wrote only
  // the newly generated sections and silently deleted everything before them.
  const processAndFinalizeSave = useCallback(async (rawMarkdown, finalStatus = 'DRAFT', prefixHtml = '') => {
    if (!rawMarkdown?.trim() || !chapterId) return
    try {
      const chNum = chapter?.chapter_number || 1
      const opts  = { thesisId: id, chapterId, chapterNum: chNum }

      // The backend persists raw markdown after every section, so a resumed
      // chapter's prefix frequently still carries [FIGURE_START] blocks.
      // Concatenating it untouched left those blocks as literal text and lost
      // every figure written before the pause. Tokenise it here instead.
      const prefix = prefixHtml?.includes('_START]')
        ? await processFiguresFromMarkdown(prefixHtml, supabase, opts)
        : (prefixHtml || '')

      // Seed the counter from what the prefix already holds. Without this the
      // new sections reissued fig_<key>_1, colliding with the figure that id
      // already belonged to; every registry downstream is keyed by token, so
      // the collision silently erased one of the two figures from the count.
      const fresh = await processFiguresFromMarkdown(rawMarkdown, supabase, { ...opts, startAt: countVisualTokens(prefix) })
      const html  = prefix + fresh
      const wc    = html.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length
      await supabase.from('chapters').update({ content: html, word_count: wc, status: finalStatus }).eq('id', chapterId)
      await syncChapterFigures(supabase, { chapterId, html })
      setStatus(finalStatus)
      setWordCount(wc)
      setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
      // Update editor with processed HTML (figures now inline as TipTap nodes)
      if (editor) editor.commands.setContent(html, false)

      // Checklist reflects what actually shipped — only meaningful once the
      // chapter has stopped changing, not on every intermediate GENERATING save.
      if (finalStatus === 'DRAFT') {
        setVisualChecklist(buildVisualChecklist({ html, chapterNumber: chNum, level: visualDensity }))
      }
    } catch (err) {
      console.error('[processAndFinalizeSave]', err)
    }
  }, [chapterId, id, chapter, editor, visualDensity]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fill missing figures/tables ─────────────────────────────────────────
  // Regenerating the whole chapter to fix one missing diagram burns 2-5x the
  // tokens a real fix needs. This asks for exactly the shortfall the
  // checklist already measured, then slots the result into the chapter that
  // already exists instead of throwing it away and starting over.
  const handleFillVisuals = useCallback(async () => {
    if (fillingVisuals || !visualChecklist || visualChecklist.complete || !editor) return
    const chNum = chapter?.chapter_number || 1

    // Recompute from the editor's live content rather than trusting the
    // banner's state — the user may have edited (or deleted a figure) since
    // it was last computed, and asking the model for a stale shortfall is
    // exactly the kind of silent mismatch this feature exists to prevent.
    const liveChecklist = buildVisualChecklist({ html: editor.getHTML(), chapterNumber: chNum, level: visualDensity })
    if (liveChecklist.complete) {
      setVisualChecklist(liveChecklist)
      return
    }
    const need = {
      figures: Math.max(0, liveChecklist.expected.figures - liveChecklist.delivered.figures),
      tables:  Math.max(0, liveChecklist.expected.tables  - liveChecklist.delivered.tables),
    }
    if (!need.figures && !need.tables) return

    setFillingVisuals(true)
    setGenerationError('')
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token

      const resp = await fetch(
        `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/thesis/${id}/chapters/${chapterId}/fill-visuals`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify(need),
        }
      )
      if (!resp.ok) {
        if (resp.status === 409) throw new Error('This chapter is already generating in another tab — wait for it to finish first.')
        const body = await resp.json().catch(() => ({}))
        throw new Error(body.error || `HTTP ${resp.status}`)
      }

      const reader  = resp.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      let raw    = ''
      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n'); buffer = lines.pop()
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const payload = line.slice(6).trim()
          if (!payload) continue
          try {
            const evt = JSON.parse(payload)
            if (evt.text) raw += evt.text
            else if (evt.error) throw new Error(evt.error)
          } catch (e) { if (e?.message && !e.message.includes('JSON')) throw e }
        }
      }

      if (!raw.trim()) throw new Error('The model returned no visuals to add — try again')

      const currentHtml = editor.getHTML()
      const html = await fillMissingVisuals(raw, currentHtml, supabase, { thesisId: id, chapterId, chapterNum: chNum })
      const wc   = html.replace(/<[^>]+>/g, ' ').trim().split(/\s+/).filter(Boolean).length

      await supabase.from('chapters').update({ content: html, word_count: wc }).eq('id', chapterId)
      setWordCount(wc)
      setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
      editor.commands.setContent(html, false)
      setVisualChecklist(buildVisualChecklist({ html, chapterNumber: chNum, level: visualDensity }))
      toast.success('Missing figures and tables added')
    } catch (err) {
      console.error('[handleFillVisuals]', err)
      toast.error(err.message || 'Could not fill missing visuals')
    } finally {
      setFillingVisuals(false)
    }
  }, [visualChecklist, editor, id, chapterId, chapter, visualDensity])

  // ── Check for research results availability (enables Research Figures button) ──
  // Re-checks on every chapter visit (not just thesis mount) — otherwise a
  // chapter opened before Research Lab was run stays stuck showing "no data"
  // forever within the same session, since this thesis-wide check never
  // re-fires on chapter switches that don't remount the page.
  useEffect(() => {
    if (!id) return
    supabase
      .from('thesis_research_results')
      .select('id', { count: 'exact', head: true })
      .eq('thesis_id', id)
      .then(({ count, error }) => {
        if (error) { console.error('[ResearchFigures] availability check failed:', error); return }
        setHasResearchResults((count || 0) > 0)
      })
  }, [id, chapterId])

  // ── Enforce TipTap editable state based on workflow status ───────────────
  useEffect(() => {
    if (!editor) return
    editor.setEditable(!EDITING_LOCKED_STATES.has(status))
  }, [editor, status])

  // ── Streaming mode — backend first, Anthropic direct fallback ────────────

  useEffect(() => {
    const isStreaming = searchParams.get('streaming') === '1'
    if (!isStreaming || !editor) return

    const resumeFrom = parseInt(searchParams.get('resume_from') || '0')

    const startStreaming = async () => {
      if (generatingRef.current) return

      // Finalized chapters must not be overwritten silently
      resumePrefixRef.current = ''
      try {
        const { data: liveChapter } = await supabase.from('chapters').select('status, content').eq('id', chapterId).single()
        if (REGEN_BLOCKED_STATES.has(liveChapter?.status) || liveChapter?.status === 'FINALIZED') {
          setGenerationError('This chapter has been finalised for export stability. Return to Draft from the Status panel to continue developing the content.')
          navigate(`/app/thesis/${id}/chapters/${chapterId}`, { replace: true })
          return
        }
        if (resumeFrom > 0) resumePrefixRef.current = liveChapter?.content || ''
      } catch { /* network hiccup — proceed */ }

      currentSectionRef.current = resumeFrom

      generatingRef.current = true

      setStreaming(true)
      setIsGenerating(true)
      setGenerationProgress(0)
      setGenerationError('')

      // Only clear editor on fresh generation, not resume
      if (resumeFrom === 0) {
        editor.commands.clearContent()
        editor.commands.focus('end')
        setWordCount(0)
      }

      // Mark as GENERATING in DB (best effort)
      try {
        await supabase.from('chapters').update({ status: 'GENERATING' }).eq('id', chapterId)
      } catch { /* ignore */ }

      accumulatedRef.current = ''
      let accumulated = ''
      let sectionStartLen = 0   // where the current section began in `accumulated`, so a polished version can replace it
      let liveBuffer  = ''

      // Figure-block filter: prevents raw [FIGURE_START]...[FIGURE_END] code
      // from appearing in the live editor. Figures are rendered after generation.
      const figFilter = { inFigure: false }
      const filterFigs = (text) => {
        const parts = text.split(/(\[FIGURE_START\]|\[FIGURE_END\]|\[TABLE_START\]|\[TABLE_END\])/)
        let out = ''
        for (const part of parts) {
          if (part === '[FIGURE_START]' || part === '[TABLE_START]') { figFilter.inFigure = true }
          else if (part === '[FIGURE_END]' || part === '[TABLE_END]') { figFilter.inFigure = false }
          else if (!figFilter.inFigure) { out += part }
        }
        return out
      }

      const flush = () => { if (liveBuffer) { editor.commands.insertContent(liveBuffer); liveBuffer = '' } }

      try {
        // ── Try backend streaming ───────────────────────────────────────────
        let backendSucceeded  = false
        let backendAborted    = false
        let streamInterrupted = null
        try {
          const { data: { session } } = await supabase.auth.getSession()
          const token = session?.access_token

          // The 8s budget covers CONNECTING only. It must be cleared once the
          // response headers arrive, otherwise it aborts the response body
          // mid-stream — which killed every generation exactly 8s in, then got
          // swallowed below as "backend unavailable" while backendSucceeded
          // stayed true, so the fallback never ran and generation just stopped.
          const backendController = new AbortController()
          const connectTimer = setTimeout(() => backendController.abort(), 8000)

          let resp
          try {
            resp = await fetch(
              `${import.meta.env.VITE_API_URL || 'http://localhost:3001/api'}/thesis/${id}/chapters/${chapterId}/generate`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                body: JSON.stringify({ resumeFrom, visualDensity, harnessWriter, harnessPolish }),
                signal: backendController.signal,
              }
            )
          } finally {
            clearTimeout(connectTimer)
          }

          if (!resp.ok) {
            if (resp.status === 409) {
              // Silently navigating away here made a blocked retry look like a
              // dead button — the user pressed Generate/Resume and nothing at
              // all happened, with no explanation.
              setGenerationError('This chapter is already generating in another tab or session. Wait for it to finish, or press Stop there first.')
              navigate(`/app/thesis/${id}/chapters/${chapterId}`, { replace: true })
              return
            }
            throw new Error(`HTTP ${resp.status}`)
          }

          backendSucceeded = true
          const backendReader = resp.body.getReader()
          abortRef.current = { abort: () => { backendAborted = true; backendReader.cancel() } }
          const decoder = new TextDecoder()
          let buffer = ''

          while (true) {
            if (!generatingRef.current) break
            const { done, value } = await backendReader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n'); buffer = lines.pop()
            for (const line of lines) {
              if (!line.startsWith('data: ')) continue
              const payload = line.slice(6).trim()
              if (payload === '[DONE]') continue
              try {
                const evt = JSON.parse(payload)
                if (evt.text) {
                  accumulated += evt.text; accumulatedRef.current += evt.text
                  const vis = filterFigs(evt.text); if (vis) liveBuffer += vis
                  if (liveBuffer.length >= 80) flush()
                } else if (typeof evt.section === 'number') {
                  // Backend section marker. Without it Pause always recorded
                  // section 0 and Resume restarted the chapter from the top.
                  currentSectionRef.current = evt.section
                  sectionStartLen = accumulated.length
                } else if (typeof evt.refined?.text === 'string' && evt.refined.section === currentSectionRef.current) {
                  // The server polished this section after streaming it. Swap it in, so what we save is what the server saved.
                  accumulated = accumulated.slice(0, sectionStartLen) + evt.refined.text
                  accumulatedRef.current = accumulated
                } else if (evt.error) throw new Error(evt.error)
              } catch (e) { if (e?.message && !e.message.includes('JSON')) throw e }
            }
          }
        } catch (e) {
          const userStopped = backendAborted || !generatingRef.current
          if (backendSucceeded && !userStopped) {
            // The stream started and then broke on its own (dropped connection,
            // Render restart, upstream error). Record it instead of swallowing
            // it silently, so the user is told it stopped early rather than
            // being left to guess why generation halted.
            streamInterrupted = e?.message || e?.name || 'connection lost'
          }
          // Never connected (or user stopped) — fall through to the direct path.
        }

        // ── Anthropic direct generation (section by section) ────────────────
        if (!backendSucceeded && generatingRef.current) {
          aiStore.assertEnabled()

          const [{ data: thesisData }, { data: chapterData }, { data: researchResults }] = await Promise.all([
            supabase.from('theses').select('*').eq('id', id).maybeSingle(),
            supabase.from('chapters').select('*').eq('id', chapterId).maybeSingle(),
            supabase.from('thesis_research_results').select('analysis_type, label, result_json').eq('thesis_id', id).order('created_at', { ascending: false }).limit(8),
          ])
          if (!thesisData || !chapterData) throw new Error('Could not load thesis data')

          // Build research data block for quantitative chapters
          const isQuantitativeChapter = /result|finding|analysis|data|quantitative|regression|statistic/i.test(chapterData.title || '')
          const researchDataBlock = isQuantitativeChapter && researchResults?.length > 0
            ? `\n\nCOMPUTED RESEARCH RESULTS — REAL DATA (CRITICAL: Use ONLY these exact numbers. Do NOT invent, round differently, or fabricate ANY statistical value. Every β, p-value, R², F-statistic shown below was genuinely computed):
${researchResults.map(r => {
  const d = r.result_json
  if (r.analysis_type === 'ols') {
    return `\n=== ${r.label || 'OLS Regression'} ===
N=${d.n} | R²=${d.r_squared} | Adj.R²=${d.adj_r_squared} | F=${d.f_statistic} (p=${d.f_pvalue}) | RMSE=${d.rmse}
Coefficients: ${Object.entries(d.coefficients || {}).map(([k, c]) => `${k}: β=${c.beta}, SE=${c.std_err}, t=${c.t_stat}, p=${c.p_value} [${c.significant ? 'sig' : 'n.s.'}]`).join('; ')}
VIF: ${Object.entries(d.vif || {}).map(([k, v]) => `${k}=${v}`).join(', ')}
Breusch-Pagan: LM=${d.breusch_pagan?.lm_statistic}, p=${d.breusch_pagan?.pvalue} (${d.breusch_pagan?.heteroscedastic ? 'heteroscedastic' : 'homoscedastic'})`
  }
  if (r.analysis_type === 'correlation') {
    const vars = d.variables || []
    return `\n=== Correlation Matrix ===\nVariables: ${vars.join(', ')}\n${vars.map(rv => vars.map(cv => d.matrix?.[rv]?.[cv] ?? '').join('\t')).join('\n')}`
  }
  if (r.analysis_type === 'descriptive') {
    return `\n=== Descriptive Statistics ===\n${Object.entries(d).map(([k, s]) => `${k}: N=${s.n}, M=${s.mean}, SD=${s.std}, range=[${s.min},${s.max}], skew=${s.skewness}`).join('\n')}`
  }
  if (r.analysis_type === 'cronbach') {
    return `\n=== Cronbach Alpha ===\nα=${d.alpha} (${d.reliability}), k=${d.n_items}, N=${d.n_obs}`
  }
  return ''
}).filter(Boolean).join('\n')}`
            : ''

          // Resume: prior prose, stripped of markup, purely as context for the
          // model. It must NOT go into `accumulated` — that is what gets saved,
          // and the already-written part is re-attached separately as
          // resumePrefixRef. Seeding it here wrote the whole earlier half of the
          // chapter a second time, in plain text, with its figures flattened.
          let priorText = ''
          if (resumeFrom > 0 && chapterData.content) {
            priorText = chapterData.content
              .replace(/<div[^>]*data-thesis-figure[^>]*><\/div>/g, '[Figure]')
              .replace(/<[^>]+>/g, ' ')
              .replace(/\s+/g, ' ')
              .trim()
          }

          const sections = Array.isArray(chapterData.toc_sections?.sections)
            ? chapterData.toc_sections.sections
            : Array.isArray(chapterData.toc_sections) ? chapterData.toc_sections : []

          // Same problem as the backend prompt: "where they genuinely aid
          // understanding" reads as permission to skip. The per-section plan
          // below turns it into an explicit per-call instruction.
          const fallbackVisualPlan = planSectionVisuals(sections.length, visualDensity)
          const VISUAL_FORMAT = `
VISUAL SYNTAX, when this section requires a visual use EXACTLY these formats:

[FIGURE_START]
TYPE: mermaid
TITLE: <descriptive title only, NEVER a figure or table number. Do NOT write "Figure 4.1" or "Table 2.3" — numbering is assigned automatically at export and a number here produces a duplicate on the page>
CODE:
graph TD
    A[Concept] --> B[Concept]
    B --> C[Outcome]
CAPTION:
Source: Author's own conceptualisation
[FIGURE_END]

[TABLE_START]
TITLE: <descriptive title only, NEVER a figure or table number. Do NOT write "Figure 4.1" or "Table 2.3" — numbering is assigned automatically at export and a number here produces a duplicate on the page>
| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| value    | value    | value    |
CAPTION:
Source: Adapted from Author (Year)
[TABLE_END]`

          // Paper type shapes the argumentative register of every section
          const PAPER_TYPE_BEHAVIORS = {
            analytical:       'Build every argument through evidence analysis. Every claim must advance an analytical position: never describe when you can analyse.',
            argumentative:    'Take and defend a clear stance throughout. Acknowledge counterarguments by name, then rebut them explicitly. Each section reinforces the central thesis.',
            interpretive:     'Foreground your interpretive moves. Make the interpretive lens explicit. Distinguish your readings from established readings in the literature.',
            compare_contrast: 'Structure arguments through systematic comparison. Use parallel structure within sections. State the basis of comparison explicitly before comparing.',
            cause_effect:     'Establish causal chains with evidence. Distinguish correlation from causation explicitly. Trace mechanisms and pathways, not just statistical associations.',
            report:           'Present findings objectively and systematically. Organise around data. Restrain normative judgements unless the data clearly supports them.',
            definition:       'Interrogate and refine conceptual boundaries. Trace definitional debates in the literature. Arrive at a working definition and defend it with precision.',
          }
          const paperType = thesisData.paper_type || 'analytical'
          const paperTypeBehavior = PAPER_TYPE_BEHAVIORS[paperType] || PAPER_TYPE_BEHAVIORS.analytical

          const PERSONA = `You are a distinguished PhD thesis writer with 25+ years of academic experience. Write publication-quality academic prose: analytical, intellectually mature, occasionally opinionated. Use uneven paragraph lengths; mix short decisive sentences with long analytical ones.

ABSOLUTE PROHIBITION, NEVER under any circumstances:
- Insert [PLACEHOLDER], [TBD], [INSERT], or any bracketed marker into the text
- Write incomplete sentences such as "The sample comprises" or "The panel spans" without finishing them
- Defer to the candidate with phrases like "to be completed", "insert here", "candidate to provide", "to be filled"
- Write "N = [sample size]" or any unresolved variable reference
- Leave incomplete ranges such as "from 2016 to" without the end year
- Write "interviews with" without specifying who was interviewed

INSTEAD, always complete every sentence confidently using:
- If COMPUTED RESEARCH RESULTS are provided below: use those exact numbers
- If thesis metadata provides values (university, year, domain, sample size): use them
- Otherwise: infer a plausible, domain-realistic value and write it as fact

EXAMPLES OF REQUIRED BEHAVIOUR:
BAD: "The sample comprises"
GOOD: "The sample comprises 120 municipal officials and policy implementers across three administrative tiers."

BAD: "The panel spans"
GOOD: "The panel spans the period from 2016 to 2024, capturing two complete electoral cycles."

BAD: "Semi-structured interviews with"
GOOD: "Semi-structured interviews were conducted with 14 senior municipal officials, environmental officers, and private sector representatives."

LANGUAGE: Never use "furthermore, moreover, additionally, to summarise, this demonstrates, it is worth noting, it should be noted". Use instead: "What matters here is…", "In practice…", "The problem emerges when…", "This distinction is critical because…"

PUNCTUATION BUDGET: at most one em-dash (—) per three paragraphs. Never use a pair of em-dashes as parenthetical brackets. Prefer a comma, colon, semicolon, or a new sentence. Overuse of the em-dash is the single most recognisable signature of machine-written prose.

PAPER TYPE BEHAVIOUR (${paperType}): ${paperTypeBehavior}

CLAIM DISCIPLINE, every claim must fit one of these modes (write to the mode, never label it):
- Literature-backed: cite with precision and confidence; do not hedge what the evidence clearly shows
- Interpretation: signal you are interpreting, not reporting ("This suggests…", "One reading of this is…")
- Argument: make the logical structure explicit through reasoning, not bare assertion
- Result/finding: state directly without excessive hedging unless genuine uncertainty exists
- Hypothesis: frame as a testable proposition, not an assumption
- Limitation: acknowledge once, specifically; do not over-caveat throughout
- Future direction: connect to an identified gap, not a generic "future research is needed"

TOPIC CONTINUITY: Once you introduce a concept or argument, develop it fully before moving on. Do not return to topics already concluded. Each paragraph advances the argument. No circular revisiting. No sandwich structure (A→B→A is never acceptable).

ACADEMIC STRUCTURE RULES:
- Place figures and tables immediately after their first in-text citation: never before
- Synthesise literature; do not list what each author said in sequence
- Every major claim is grounded in evidence or explicitly framed as interpretation
- Use signposting to guide the reader through complex arguments ("Having established X, the next question is…")
- Prefer active voice where it is clearer than passive`

          const THESIS_CTX = `THESIS: "${thesisData.title}"
University: ${thesisData.university || ''} | Programme: ${thesisData.programme || ''}
Methodology: ${thesisData.methodology || ''} | Domain: ${thesisData.domain || ''}
Geographic Focus: ${thesisData.geographic_focus || ''}
Paper Type: ${paperType}`

          // 7-step story-driven introduction structure
          const isIntroChapter = chapterData.chapter_number === 1
            || /intro/i.test(chapterData.title || '')

          const INTRO_STRUCTURE = isIntroChapter ? `
INTRODUCTION STRUCTURE, write these 7 steps as continuous flowing prose (no step-label subheadings):
1. CONTEXT: Establish the broad domain and its real-world significance (2–3 paragraphs)
2. PROBLEM: Narrow precisely to the specific problem this thesis addresses (1–2 paragraphs)
3. LITERATURE LIMITATION: Identify what existing research has failed to address or resolve (1–2 paragraphs)
4. GAP: State the gap this thesis fills: one precise, unhedged paragraph
5. INSIGHT: State the thesis's core claim or contribution (1–2 paragraphs)
6. STRUCTURE: Describe thesis organisation chapter by chapter (one paragraph per chapter)
7. SCOPE: State geographic, temporal, or methodological boundaries of this study
` : ''

          // Goes through the backend AI proxy, same as every other inference
          // call. This path used to hit api.anthropic.com directly with a key
          // taken from import.meta.env — which put that key in the public
          // bundle. The browser holds no credential now.
          // Returns the text this specific call produced (not the running
          // chapter total) so callers can check it against its own visual
          // directive — the backend's streamPassage does the same thing for
          // exactly the same reason.
          const makeAnthropicCall = async (prompt) => {
            const controller = new AbortController()
            abortRef.current = controller
            let callText = ''
            try {
              await streamAnthropic({
                feature:   'chapter_generate',
                maxTokens: 8000,
                messages:  [{ role: 'user', content: prompt }],
                signal:    controller.signal,
                onChunk: (text) => {
                  if (!generatingRef.current) { controller.abort(); return }
                  callText += text
                  accumulated += text; accumulatedRef.current += text
                  const vis = filterFigs(text); if (vis) liveBuffer += vis
                  if (liveBuffer.length >= 80) flush()
                },
              })
            } catch (err) {
              // Stopping is not a failure. Letting the abort propagate would
              // skip the save below and throw away everything written so far —
              // the exact progress loss Pause and Stop exist to prevent.
              if (err?.name !== 'AbortError') throw err
            }
            flush()
            return callText
          }

          // The backend path (streamPassage, thesis.controller.js) checks every
          // passage against its own visual directive and fires one bounded
          // repair call when short — the model routinely reads "include N
          // figures" as a suggestion, not a requirement, so nothing enforces the
          // count without this. This fallback path only ran the one prompt and
          // trusted the model, which is why a chapter generated here (every
          // Render cold start over ~8s routes here, not through the backend)
          // could land with the count reported correctly by the checklist
          // afterward but zero figures actually in the prose: the checklist was
          // never the thing that was broken.
          const repairMissingVisuals = async (directive, passage) => {
            const missing = missingVisuals(directive, passage)
            if (!missing.figures && !missing.tables) return
            const word = (n) => ({ 1: 'ONE', 2: 'TWO', 3: 'THREE', 4: 'FOUR', 5: 'FIVE' }[n] || n)
            const wants = []
            if (missing.figures) wants.push(`exactly ${word(missing.figures)} Mermaid ${missing.figures === 1 ? 'figure' : 'figures'}`)
            if (missing.tables) wants.push(`exactly ${word(missing.tables)} comparison ${missing.tables === 1 ? 'table' : 'tables'}`)
            const repairPrompt = `You are adding visuals to a PhD thesis passage that is already written and must not be changed.

THESIS: "${thesisData.title}" | CHAPTER ${chapterData.chapter_number}: ${chapterData.title}

THE PASSAGE:
"""
${passage.slice(-4000)}
"""

VISUAL REQUIREMENT: you MUST include ${wants.join(' and ')}, each depicting something this passage actually discusses. Place each one where it genuinely clarifies the argument.
${VISUAL_FORMAT}

Output ONLY the visual block${(missing.figures + missing.tables) === 1 ? '' : 's'}. No prose, no preamble, no commentary, nothing before the first marker and nothing after the last one.`
            if (!generatingRef.current) return
            await makeAnthropicCall(repairPrompt)
          }

          if (sections.length === 0) {
            setGenerationProgress(10)
            const targetWords = chapterData.target_words || 8000
            const prompt = `${PERSONA}

${THESIS_CTX}
${researchDataBlock}
CHAPTER ${chapterData.chapter_number}: ${chapterData.title}
${chapterData.user_notes ? `\nCANDIDATE INSTRUCTIONS:\n${chapterData.user_notes}\n` : ''}
TARGET LENGTH: approximately ${targetWords} words
VISUALS: include exactly ${VISUAL_LEVELS[clampVisualLevel(visualDensity)].figures} Mermaid figures and ${VISUAL_LEVELS[clampVisualLevel(visualDensity)].tables} comparison tables across this chapter, spread across different sections. Reference each one in the surrounding prose.
${VISUAL_FORMAT}
${INTRO_STRUCTURE}
Use APA 7th Edition in-text citations. Write the complete chapter starting with the chapter title as an H1 heading, then all sections with H2 headings.
Begin now:`
            const chapterText = await makeAnthropicCall(prompt)
            await repairMissingVisuals(VISUAL_LEVELS[clampVisualLevel(visualDensity)], chapterText)
            setGenerationProgress(90)
          } else {
            const totalSecs = sections.length

            for (let si = resumeFrom; si < sections.length; si++) {
              if (!generatingRef.current) break
              currentSectionRef.current = si
              const sec = sections[si]
              setGenerationProgress(Math.round(((si - resumeFrom) / (totalSecs - resumeFrom)) * 90))

              const subsCtx = sec.subsections?.length
                ? ` Sub-topics: ${sec.subsections.map(s => `${s.number} ${s.title}`).join(' | ')}.`
                : ''
              const wordsPerSec = Math.round((chapterData.target_words || 8000) / totalSecs)

              // For resume/continuation, give model the prior written text as context
              const written  = `${priorText} ${accumulated}`.trim()
              const priorCtx = (si > 0 || resumeFrom > 0) && written
                ? `\n\nCONTEXT: The following has already been written for this chapter. Do NOT repeat it; continue directly:\n---\n${written.slice(-3000)}\n---\n`
                : ''

              // Section goal opening: every section begins with a purpose statement
              const SECTION_GOAL = `SECTION OPENING: Begin with a single declarative sentence that states the specific purpose or argument of this section: not "this section will…" but a direct analytical claim or move that orients the reader before the content begins.`

              // Introduction chapters get the 7-step story-driven structure
              const sectionIntroStructure = isIntroChapter ? INTRO_STRUCTURE : ''

              const prompt = `${PERSONA}

${THESIS_CTX}
${researchDataBlock}
CHAPTER ${chapterData.chapter_number}: ${chapterData.title}
SECTION: ${sec.number} ${sec.title}${subsCtx}
${chapterData.user_notes ? `\nCANDIDATE INSTRUCTIONS:\n${chapterData.user_notes}\n` : ''}
TARGET LENGTH: approximately ${wordsPerSec} words
${SECTION_GOAL}
${visualInstructionFor(fallbackVisualPlan[si])}
${VISUAL_FORMAT}
${sectionIntroStructure}${priorCtx}
Use APA 7th Edition in-text citations. Start with the APA Level 2 heading: ${sec.number} ${sec.title}
Begin now:`

              const sectionText = await makeAnthropicCall(prompt)
              await repairMissingVisuals(fallbackVisualPlan[si], sectionText)
            }
          }
        }

        // ── Post-process and save ───────────────────────────────────────────
        flush()
        if (accumulated.trim()) {
          setGenerationProgress(100)
          aiStore.increment()
          if (streamInterrupted) {
            // Keep GENERATING + the section marker so the Resume banner appears,
            // same as a manual pause — the work so far is not thrown away.
            localStorage.setItem(`acdyon_pause_${chapterId}`, JSON.stringify({ sectionIndex: currentSectionRef.current }))
            await processAndFinalizeSave(accumulated, 'GENERATING', resumePrefixRef.current)
          } else {
            // Convert markdown → HTML, extract figures → DB, render inline in editor
            await processAndFinalizeSave(accumulated, 'DRAFT', resumePrefixRef.current)
            localStorage.removeItem(`acdyon_pause_${chapterId}`)
          }
        }

        if (streamInterrupted) {
          setGenerationError(`Generation stopped early (${streamInterrupted}). Your progress was saved — press Resume to continue.`)
          return
        }

        navigate(`/app/thesis/${id}/chapters/${chapterId}`, { replace: true })
      } catch (err) {
        if (err?.name !== 'AbortError') {
          console.error('Generation error:', err)
          setGenerationError(err?.message || 'Generation failed. Check your connection and try again.')
        }
      } finally {
        generatingRef.current = false
        setStreaming(false)
        setIsGenerating(false)
        setGenerationProgress(0)
      }
    }

    startStreaming()
  }, [searchParams, editor]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save ─────────────────────────────────────────────────────────────────

  // Stable scheduler — calls via saveRef so it always uses the latest save()
  // regardless of when status or other deps changed.
  const scheduleSave = useCallback(() => {
    clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => saveRef.current?.(), 3000)
  }, [])

  const save = useCallback(async () => {
    if (!editor || !chapterId) return
    setSaving(true)
    try {
      const html        = editor.getHTML()
      const wc          = editor.getText().trim().split(/\s+/).filter(Boolean).length
      let   activeStatus = status

      // Only invalidate approval for MAJOR edits — minor fixes (typos, spacing) preserve status
      if (activeStatus === 'APPROVED') {
        const { type: editType, reason } = classifyEdit(previousHtmlRef.current, html)
        if (editType === 'MAJOR') {
          const newStatus = await invalidateApprovalOnEdit({
            thesisId: id, chapterId, currentStatus: activeStatus, contentHtml: html,
          })
          if (newStatus) {
            activeStatus = newStatus
            setStatus(newStatus)
            toast(`Chapter returned to Draft — ${reason}. Minor edits like typos and spacing preserve approval.`, { icon: '✏️', duration: 5000 })
          }
        }
      }
      previousHtmlRef.current = html

      try {
        await api.put(`/thesis/${id}/chapters/${chapterId}`, { content: html, word_count: wc, status: activeStatus })
      } catch {
        await supabase.from('chapters').update({ content: html, word_count: wc, status: activeStatus }).eq('id', chapterId)
      }
      setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
    } catch { /* silent */ } finally {
      setSaving(false)
    }
  }, [editor, id, chapterId, status])

  // Keep saveRef current so scheduleSave always calls the latest save()
  useEffect(() => { saveRef.current = save }, [save])

  // Cmd+S / Ctrl+S shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        save()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [save])

  // Warn on unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (saveTimerRef.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  // ── Status change — DB write is handled by chapterWorkflowEngine inside the badge ──

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus)
  }

  // ── Visual density ────────────────────────────────────────────────────────
  // The slider applies immediately in local state so the next Generate uses it
  // even if persistence fails (e.g. before migration 020 has been run).

  const handleHarnessWriterChange = (on) => {
    setHarnessWriter(on)
    try { localStorage.setItem('acdyon_harness_writer', on ? 'on' : 'off') } catch { /* private window: the choice just won't be remembered */ }
  }

  const handleHarnessPolishChange = (on) => {
    setHarnessPolish(on)
    try { localStorage.setItem('acdyon_harness_polish', on ? 'on' : 'off') } catch { /* private window: the choice just won't be remembered */ }
  }

  const handleVisualDensityChange = async (level) => {
    const next = clampVisualLevel(level)
    setVisualDensity(next)
    try {
      await api.put(`/thesis/${id}/chapters/${chapterId}`, { visual_density: next })
    } catch (err) {
      console.warn('[VisualDensity] Could not persist setting:', err?.response?.data?.error || err.message)
    }
  }

  // ── Inline AI transform ───────────────────────────────────────────────────

  const handleInlineTransform = async (mode) => {
    if (!editor) return
    const { from, to } = editor.state.selection
    if (from === to) return

    const selectedText = editor.state.doc.textBetween(from, to, ' ')
    if (!selectedText.trim()) return

    setTransforming(true)
    try {
      const instructions = {
        expand:   'Expand this academic text — add depth, supporting detail, and scholarly nuance. Keep the same point of view. Return only the expanded text, no preamble.',
        simplify: 'Simplify this academic text — make it clearer and more concise without losing the academic voice. Return only the simplified text, no preamble.',
        rephrase: 'Rephrase this academic text — keep the meaning but vary the sentence structure and word choice. Return only the rephrased text, no preamble.',
      }
      const sysPrompt = `You are an expert academic editor working on a doctoral thesis titled "${thesis?.title || ''}". Chapter: "${chapter?.title || ''}".`
      const result = await generateAnthropic({
        feature:   'inline_transform',
        maxTokens: Math.max(selectedText.split(/\s+/).length * 3, 200),
        system:    sysPrompt,
        messages:  [{ role: 'user', content: `${instructions[mode] || instructions.rephrase}\n\n${selectedText}` }],
      })
      if (result) {
        editor.chain().focus().deleteRange({ from, to }).insertContentAt(from, result).run()
        aiStore.increment()
      }
    } catch { /* silent */ } finally {
      setTransforming(false)
    }
  }

  // ── Deduplicate content ───────────────────────────────────────────────────

  const handleDeduplicate = useCallback(async () => {
    if (!editor) return
    const raw  = editor.getHTML()
    const clean = removeDuplicateSections(deduplicateChapterContent(raw))
    editor.commands.setContent(clean, false)
    const wc = editor.getText().trim().split(/\s+/).filter(Boolean).length
    setWordCount(wc)
    await save()
  }, [editor, save]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Export chapter ────────────────────────────────────────────────────────
  // All formats build client-side from the live editor content — figures
  // (mermaid diagrams, SVG charts, tables) only exist as real content in the
  // browser (TipTap NodeViews), so exporting via the backend from raw DB
  // content always lost them. HTML/PDF render them for real (mermaid.js,
  // native <svg>, <table>); DOCX embeds them as real tables/images via the
  // `docx` package.

  const handleExport = useCallback(async (format = 'html') => {
    if (!editor) return
    const chNum = chapter?.chapter_number || 1
    const title = chapter?.title || `Chapter ${chNum}`
    const editorHtml = editor.getHTML()

    if (format === 'html') {
      const full = await buildChapterExportHtml(editorHtml, title)
      const blob = new Blob([full], { type: 'text/html;charset=utf-8' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `chapter-${chNum}.html`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
      return
    }

    if (format === 'pdf') {
      // Same HTML as the .html export, with diagrams already inlined as SVG.
      // Printing it produces a PDF containing real figures.
      const full = await buildChapterExportHtml(editorHtml, title)
      const win  = window.open('', '_blank')
      if (!win) {
        import('react-hot-toast').then(({ default: toast }) => toast.error('Allow pop-ups for this site to export PDF.'))
        return
      }
      win.document.write(full)
      win.document.close()
      win.focus()
      // Diagrams are already inline SVG, so there is no CDN render to wait on;
      // one frame is enough for layout.
      setTimeout(() => win.print(), 250)
      return
    }

    // DOCX — client-side, embeds real tables/images
    try {
      const { generateSingleChapterDocxBlob } = await import('../../utils/docxExporter')
      // thesis/fm fill the DOCX core properties, so File → Properties shows the
      // candidate and their university rather than a library default.
      const { data: fmRow } = await supabase.from('thesis_front_matter').select('student_name, university_name').eq('thesis_id', id).maybeSingle()
      const blob = await generateSingleChapterDocxBlob(
        { chapter_number: chNum, title, content: editorHtml },
        { thesis, fm: fmRow },
      )
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `chapter-${chNum}.docx`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export error:', err)
      import('react-hot-toast').then(({ default: toast }) => toast.error(`Export failed: ${err.message}`))
    }
  }, [editor, chapter])

  // ── Regenerate figures ────────────────────────────────────────────────────
  // Re-runs the figure pipeline on the current editor HTML. This re-extracts
  // any [FIGURE_START] / [TABLE_START] blocks still in the content (e.g. from
  // paste or older generation), upserts them to thesis_figures, and re-renders
  // inline TipTap nodes. Useful if figures failed to persist on first save.

  const handleRegenFigures = useCallback(async () => {
    if (!editor || !chapterId) return
    if (isRegenBlocked) {
      toast('This chapter has been finalised. Return to Draft from the Status panel to regenerate figures.', { icon: '🔒' })
      return
    }
    setRegenFiguresLoading(true)
    try {
      const html = editor.getHTML()
      // Re-run the full figure pipeline on whatever is in the editor
      const chNum = chapter?.chapter_number || 1
      const reprocessed = await processFiguresFromMarkdown(html, supabase, {
        thesisId: id, chapterId, chapterNum: chNum, startAt: countVisualTokens(html),
      })
      editor.commands.setContent(reprocessed, false)
      await supabase.from('chapters').update({ content: reprocessed }).eq('id', chapterId)
      await syncChapterFigures(supabase, { chapterId, html: reprocessed })
      toast.success('Figures re-processed and saved')
    } catch (err) {
      toast.error('Figure regeneration failed: ' + (err.message || 'unknown error'))
    } finally {
      setRegenFiguresLoading(false)
    }
  }, [editor, chapterId, id, chapter, status])

  // ── Pipeline A: Conceptual figures (Ch1-3) ───────────────────────────────────

  const handleConceptualFigures = useCallback(async () => {
    if (!editor || !chapterId) return
    if (isRegenBlocked) {
      toast('This chapter has been finalised. Return to Draft to add new figures.', { icon: '🔒' })
      return
    }
    setConceptualFiguresLoading(true)
    try {
      const ch = { id: chapterId, title: chapter?.title || '', content: editor.getHTML(), chapter_number: chapter?.chapter_number || 1 }
      const { html, injected, skipped } = generateConceptualFigures(ch, { thesis })

      if (injected.length === 0) {
        const reason = skipped[0] || 'no conceptual cues detected in chapter content'
        toast.success(`No conceptual figures added — ${reason}.`)
        return
      }

      editor.commands.setContent(html, false)
      await supabase.from('chapters').update({ content: html }).eq('id', chapterId)
      toast.success(`Added ${injected.length} conceptual figure(s): ${injected.map(f => f.figureType).join(', ')}.`)
      if (skipped.length) console.warn('[ConceptualFigures] skipped:', skipped)
    } catch (err) {
      console.error('[ConceptualFigures]', err)
      toast.error('Conceptual figure generation failed: ' + (err.message || 'unknown error'))
    } finally {
      setConceptualFiguresLoading(false)
    }
  }, [editor, chapterId, chapter, thesis, status])

  // ── Pipeline B: Research figures (Ch4+) ──────────────────────────────────────

  const handleResearchFigures = useCallback(async () => {
    if (!editor || !chapterId) return
    if (isRegenBlocked) {
      toast('This chapter has been finalised. Return to Draft to add research figures.', { icon: '🔒' })
      return
    }
    setResearchFiguresLoading(true)
    try {
      const [{ data: resRows }, { data: dsRows }] = await Promise.all([
        supabase.from('thesis_research_results').select('analysis_type, result_json').eq('thesis_id', id).order('created_at', { ascending: false }).limit(8),
        supabase.from('thesis_research_datasets').select('variables_config').eq('thesis_id', id).order('created_at', { ascending: false }).limit(1),
      ])

      if (!resRows?.length) {
        toast.error('No Research Lab results found. Run the Research Lab first.')
        return
      }

      const researchResults = Object.fromEntries(resRows.map(r => [r.analysis_type, r.result_json]))
      const variables = dsRows?.[0]?.variables_config || []
      const ch = { id: chapterId, title: chapter?.title || '', content: editor.getHTML(), chapter_number: chapter?.chapter_number || 1 }
      const { html, injected, skipped } = generateResearchFigures(ch, { researchResults, variables, thesis })

      if (injected.length === 0) {
        const reason = skipped[0] || 'no matching research cues detected'
        toast.success(`No research figures added — ${reason}.`)
        return
      }

      editor.commands.setContent(html, false)
      await supabase.from('chapters').update({ content: html }).eq('id', chapterId)
      toast.success(`Injected ${injected.length} research figure(s): ${injected.map(f => f.figureType).join(', ')}.`)
      if (skipped.length) console.warn('[ResearchFigures] skipped:', skipped)
    } catch (err) {
      console.error('[ResearchFigures]', err)
      toast.error('Research figure injection failed: ' + (err.message || 'unknown error'))
    } finally {
      setResearchFiguresLoading(false)
    }
  }, [editor, chapterId, id, chapter, thesis, status])

  // ── Repair semantic gaps ──────────────────────────────────────────────────
  // Detects and repairs incomplete academic sentences in the current chapter.

  const handleRepairSemantic = useCallback(async () => {
    if (!editor || !chapterId) return
    if (isRegenBlocked) {
      toast('This chapter has been finalised. Return to Draft to repair semantic gaps.', { icon: '🔒' })
      return
    }
    setRepairSemanticLoading(true)
    try {
      const html    = editor.getHTML()
      const issues  = detectIncompleteAcademicStructures(html)
      if (issues.length === 0) {
        toast.success('No incomplete academic structures detected in this chapter.')
        return
      }

      const repaired = repairIncompleteStructures(html, {
        thesis,
        researchConfig: { domain: thesis?.domain, sampleSize: thesis?.sample_size },
      })

      editor.commands.setContent(repaired, false)
      await supabase.from('chapters').update({ content: repaired }).eq('id', chapterId)
      const critCount = issues.filter(i => i.severity === 'critical').length
      toast.success(`Repaired ${issues.length} incomplete structure(s) (${critCount} critical) in this chapter.`)
    } catch (err) {
      console.error('[RepairSemantic]', err)
      toast.error('Semantic repair failed: ' + (err.message || 'unknown error'))
    } finally {
      setRepairSemanticLoading(false)
    }
  }, [editor, chapterId, id, chapter, thesis, status])

  // ── Stop streaming ────────────────────────────────────────────────────────

  // Aborting the browser reader alone left the backend generating for minutes,
  // and it then overwrote the chapter with a full DRAFT — undoing the pause —
  // while blocking every retry with 409. Tell the server too.
  const cancelBackendGeneration = async (status) => {
    try {
      await api.patch(`/thesis/${id}/chapters/${chapterId}/status`, { status, cancelGeneration: true })
    } catch { /* backend may not be reachable — local abort still applies */ }
  }

  const stopStreaming = async () => {
    generatingRef.current = false
    try { abortRef.current?.abort?.() } catch { /* ignore */ }
    setStreaming(false)
    setIsGenerating(false)
    setGenerationProgress(0)
    await cancelBackendGeneration('DRAFT')

    const raw = accumulatedRef.current.trim()
    if (raw) {
      // Process figures inline + save HTML to DB
      await processAndFinalizeSave(raw, 'DRAFT', resumePrefixRef.current)
      localStorage.removeItem(`acdyon_pause_${chapterId}`)
    }
    navigate(`/app/thesis/${id}/chapters/${chapterId}`, { replace: true })
  }

  // ── Pause streaming (save progress + section index for later resume) ──────
  // Paused content is saved as processed HTML (figures inline) with status
  // GENERATING, so the resume banner appears on the chapter editor.

  const pauseStreaming = async () => {
    const sectionIdx = currentSectionRef.current
    generatingRef.current = false
    try { abortRef.current?.abort?.() } catch { /* ignore */ }
    setStreaming(false)
    setIsGenerating(false)
    setGenerationProgress(0)
    await cancelBackendGeneration('GENERATING')

    // The marker is written even with no new text: a pause during the very
    // first section still has to resume from where it stopped, not restart.
    localStorage.setItem(`acdyon_pause_${chapterId}`, JSON.stringify({ sectionIndex: sectionIdx }))
    const raw = accumulatedRef.current.trim()
    if (raw) {
      // Save with GENERATING status so resume banner appears
      await processAndFinalizeSave(raw, 'GENERATING', resumePrefixRef.current)
    }
    navigate(`/app/thesis/${id}/chapters/${chapterId}`, { replace: true })
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const pausedAtSection = (() => {
    try {
      const saved = localStorage.getItem(`acdyon_pause_${chapterId}`)
      const idx   = saved ? JSON.parse(saved)?.sectionIndex : null
      return Number.isInteger(idx) ? idx : null
    } catch { return null }
  })()

  if (loading) {
    return (
      <div className="flex items-center justify-center -m-6" style={{ height: 'calc(100% + 3rem)' }}>
        <div className="w-6 h-6 rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
      </div>
    )
  }

  return (
    <>
      {/* Inject editor styles */}
      <style>{EDITOR_STYLES}</style>

      {/* -m-6 cancels AppShell's <main> padding and the +3rem restores it as
          height, so this fills exactly what main gives it — no guessed vh
          math, no leftover few px that used to force main's own scrollbar. */}
      <div className="flex -m-6" style={{ height: 'calc(100% + 3rem)', overflow: 'hidden' }}>

        {/* Left — section navigator */}
        <SectionNavigator chapter={chapter} wordCount={wordCount} />

        {/* Centre — editor */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden" style={{ background: '#060B14' }}>
          {/* Toolbar */}
          <EditorToolbar
            editor={editor}
            wordCount={wordCount}
            saving={saving}
            onSave={save}
            onInlineTransform={handleInlineTransform}
            transforming={transforming}
            thesisId={id}
            chapterTitle={chapter?.title}
            prevChapter={(isGenerating || fillingVisuals) ? null : prevChapter}
            nextChapter={(isGenerating || fillingVisuals) ? null : nextChapter}
            onGoToChapter={(chId) => navigate(`/app/thesis/${id}/chapters/${chId}`)}
          />

          {/* Floating AI bubble menu */}
          <FloatingAIMenu
            editor={editor}
            onTransform={handleInlineTransform}
            transforming={transforming}
          />

          {/* Lock banner for APPROVED / LOCKED chapters */}
          {isEditingLocked && (
            <div
              className="shrink-0 flex items-center gap-3 px-6 py-2.5"
              style={{
                background: status === 'LOCKED' ? 'rgba(124,58,237,0.12)' : 'rgba(22,163,74,0.10)',
                borderBottom: `1px solid ${status === 'LOCKED' ? 'rgba(124,58,237,0.3)' : 'rgba(22,163,74,0.25)'}`,
              }}
            >
              <Lock style={{ width: 13, height: 13, color: status === 'LOCKED' ? '#7c3aed' : '#16a34a', flexShrink: 0 }} />
              <span className="font-sans text-xs font-medium" style={{ color: status === 'LOCKED' ? '#7c3aed' : '#15803d' }}>
                {status === 'LOCKED'
                  ? 'This chapter has been submitted for final review. Use the Status panel to return to Approved if further revisions are needed.'
                  : 'This chapter has been finalised for export stability. Minor edits such as punctuation and spacing are still permitted.'}
              </span>
            </div>
          )}

          {/* Scrollable parchment area — hover-reveal scrollbar like the
              sidebar, from the global default (removed the always-hidden
              .no-scrollbar override that was here before). */}
          <div
            className="flex-1 overflow-y-auto"
            style={{ background: '#1A1410' }}
          >
            <div
              className="mx-auto my-8"
              style={{
                maxWidth: 740,
                background: '#F5F0E8',
                padding: '48px 64px',
                minHeight: 'calc(100vh - 8rem)',
                boxShadow: '0 0 60px rgba(0,0,0,0.6)',
                fontFamily: "'Source Serif 4', Georgia, serif",
                fontSize: 14.5,
                lineHeight: 1.75,
                color: '#1A1410',
              }}
            >
              {/* Figures now render inline as TipTap nodes — no separate panel needed */}
              <EditorContent editor={editor} />
            </div>
          </div>

          {/* Visual checklist — confirms what was actually generated */}
          {visualChecklist && !isGenerating && (
            <div
              className="shrink-0 px-6 py-3 font-sans text-sm"
              style={{
                background: visualChecklist.complete ? 'rgba(34,197,94,0.08)' : 'rgba(234,179,8,0.1)',
                borderTop: `1px solid ${visualChecklist.complete ? 'rgba(34,197,94,0.3)' : 'rgba(234,179,8,0.35)'}`,
              }}
            >
              <div className="flex items-center justify-between">
                <span style={{ color: visualChecklist.complete ? '#4ade80' : '#facc15' }}>
                  {visualChecklist.complete ? '✓' : '⚠'} {visualChecklist.delivered.figures} figure{visualChecklist.delivered.figures === 1 ? '' : 's'} · {visualChecklist.delivered.tables} table{visualChecklist.delivered.tables === 1 ? '' : 's'} generated
                  {!visualChecklist.complete && ` — planned ${visualChecklist.expected.figures} figure${visualChecklist.expected.figures === 1 ? '' : 's'} · ${visualChecklist.expected.tables} table${visualChecklist.expected.tables === 1 ? '' : 's'}`}
                </span>
                <div className="flex items-center gap-3 shrink-0 ml-4">
                  {(visualChecklist.delivered.figures < visualChecklist.expected.figures
                    || visualChecklist.delivered.tables < visualChecklist.expected.tables) && (
                    <button
                      onClick={handleFillVisuals}
                      disabled={fillingVisuals || isEditingLocked}
                      title={isEditingLocked ? 'Revert to Draft to add visuals' : 'Generate only the missing figures/tables and slot them into this chapter — no full regeneration'}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-sans text-xs font-semibold transition-all disabled:opacity-40"
                      style={{ background: 'rgba(234,179,8,0.15)', color: '#eab308', border: '1px solid rgba(234,179,8,0.4)' }}
                      onMouseEnter={e => { if (!fillingVisuals && !isEditingLocked) e.currentTarget.style.background = 'rgba(234,179,8,0.25)' }}
                      onMouseLeave={e => (e.currentTarget.style.background = 'rgba(234,179,8,0.15)')}
                    >
                      {fillingVisuals
                        ? <Loader2 style={{ width: 13, height: 13 }} className="animate-spin" />
                        : <ImageIcon style={{ width: 13, height: 13 }} />}
                      {fillingVisuals ? 'Filling…' : 'Fill Figures & Tables'}
                    </button>
                  )}
                  {nextChapter && !fillingVisuals && nextChapterNeedsWork === 'missing' && (
                    <button
                      onClick={() => navigate(`/app/thesis/${id}/chapters/${nextChapter.id}?streaming=1`)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-sans text-xs font-semibold transition-all"
                      style={{ background: '#2563eb', color: '#fff' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#1d4ed8')}
                      onMouseLeave={e => (e.currentTarget.style.background = '#2563eb')}
                    >
                      Generate Next Chapter →
                    </button>
                  )}
                  {nextChapter && !fillingVisuals && nextChapterNeedsWork === 'incomplete' && (
                    <button
                      onClick={() => navigate(`/app/thesis/${id}/chapters/${nextChapter.id}`)}
                      title="Next chapter is short on figures/tables — opens it so you can regenerate them from there"
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-sans text-xs font-semibold transition-all"
                      style={{ background: 'rgba(234,179,8,0.15)', color: '#eab308', border: '1px solid rgba(234,179,8,0.4)' }}
                      onMouseEnter={e => (e.currentTarget.style.background = 'rgba(234,179,8,0.25)')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'rgba(234,179,8,0.15)')}
                    >
                      Review Next Chapter →
                    </button>
                  )}
                  <button
                    onClick={() => setVisualChecklist(null)}
                    className="text-xs"
                    style={{ color: '#9ca3af' }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
              {!visualChecklist.complete && (
                <ul className="mt-2 pl-4 list-disc" style={{ color: '#d1d5db' }}>
                  {visualChecklist.issues.map((issue, i) => <li key={i}>{issue}</li>)}
                </ul>
              )}
            </div>
          )}

          {/* Generation error banner */}
          {generationError && !isGenerating && (
            <div
              className="shrink-0 flex items-center justify-between px-6 py-3"
              style={{ background: 'rgba(239,68,68,0.1)', borderTop: '1px solid rgba(239,68,68,0.3)' }}
            >
              <span className="font-sans text-sm" style={{ color: '#fca5a5' }}>
                ⚠ {generationError}
              </span>
              <button
                onClick={() => setGenerationError('')}
                className="font-sans text-xs ml-4 shrink-0"
                style={{ color: '#f87171' }}
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Resume button — shown when chapter is paused (GENERATING status, not currently streaming).
              Read from localStorage, not currentSectionRef: the ref resets to 0
              on every remount, so a reload showed "section 1" for a chapter
              paused at section 9. */}
          {status === 'GENERATING' && !isGenerating && (
            <div
              className="shrink-0 flex items-center justify-between px-6 py-3"
              style={{ background: 'rgba(37,99,235,0.08)', borderTop: '1px solid rgba(37,99,235,0.25)' }}
            >
              <span className="font-sans text-sm" style={{ color: '#93c5fd' }}>
                {pausedAtSection == null
                  // No resume point recorded (paused in another browser, or the
                  // server dropped the run) — resuming restarts the chapter, so
                  // say that instead of promising a continuation.
                  ? 'Generation stopped — no resume point saved, continuing will regenerate this chapter from the start'
                  : `Generation paused — content saved up to section ${pausedAtSection + 1}`}
              </span>
              <button
                onClick={() => {
                  const from = pausedAtSection ?? 0
                  navigate(`/app/thesis/${id}/chapters/${chapterId}?streaming=1&resume_from=${from}`, { replace: true })
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-sans text-xs font-semibold transition-all"
                style={{ background: '#2563eb', color: '#fff' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#1d4ed8')}
                onMouseLeave={e => (e.currentTarget.style.background = '#2563eb')}
              >
                ▶ Resume Generation
              </button>
            </div>
          )}

          {/* Generation progress overlay */}
          {isGenerating && (
            <div
              className="shrink-0 flex flex-col"
              style={{ background: 'rgba(15,23,42,0.92)', borderTop: '1px solid rgba(37,99,235,0.3)' }}
            >
              {/* Progress bar */}
              <div className="h-0.5 w-full" style={{ background: 'rgba(37,99,235,0.15)' }}>
                <div
                  className="h-full transition-all duration-500"
                  style={{ width: `${generationProgress}%`, background: '#2563eb' }}
                />
              </div>
              <div className="flex items-center justify-between px-6 py-3">
                <div className="flex items-center gap-3">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: '#2563eb', animation: 'pulse 1.5s ease-in-out infinite' }}
                  />
                  <span className="font-sans text-sm" style={{ color: '#e2e8f0' }}>
                    Writing Chapter {chapter?.chapter_number}
                    {currentSectionRef.current > 0 ? ` — section ${currentSectionRef.current + 1}` : ''}…
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-xs" style={{ color: '#94a3b8' }}>
                    {wordCount.toLocaleString()} words
                  </span>
                  <button
                    onClick={pauseStreaming}
                    className="px-3 py-1 rounded-lg font-sans text-xs font-medium transition-colors"
                    style={{ color: '#fbbf24', border: '1px solid rgba(251,191,36,0.3)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(251,191,36,0.1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    ⏸ Pause
                  </button>
                  <button
                    onClick={stopStreaming}
                    className="px-3 py-1 rounded-lg font-sans text-xs font-medium transition-colors"
                    style={{ color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}
                    onMouseEnter={e => (e.currentTarget.style.background = 'rgba(248,113,113,0.1)')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    ■ Stop
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right — metadata + tools */}
        <RightPanel
          chapter={chapter}
          thesisId={id}
          wordCount={wordCount}
          status={status}
          onStatusChange={handleStatusChange}
          saving={saving}
          lastSaved={lastSaved}
          onDeduplicate={handleDeduplicate}
          onExport={handleExport}
          onRegenFigures={handleRegenFigures}
          regenFiguresLoading={regenFiguresLoading}
          onConceptualFigures={handleConceptualFigures}
          conceptualFiguresLoading={conceptualFiguresLoading}
          onResearchFigures={handleResearchFigures}
          researchFiguresLoading={researchFiguresLoading}
          hasResearchResults={hasResearchResults}
          onRepairSemantic={handleRepairSemantic}
          repairSemanticLoading={repairSemanticLoading}
          isEditingLocked={isEditingLocked}
          visualDensity={visualDensity}
          onVisualDensityChange={handleVisualDensityChange}
          harnessWriter={harnessWriter}
          onHarnessWriterChange={handleHarnessWriterChange}
          harnessPolish={harnessPolish}
          onHarnessPolishChange={handleHarnessPolishChange}
          onRegenerateChapter={() => navigate(`/app/thesis/${id}/chapters/${chapterId}?streaming=1`)}
          getText={() => editor?.getText() || ''}
        />
      </div>

      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); opacity: 0.6; }
          50% { transform: translateY(-4px); opacity: 1; }
        }
      `}</style>
    </>
  )
}
